'use strict';

import VZAddBase from './VZAddBase';
import { getJourney, clearJourney } from 'Root/journey/journeyHelper';
import { chunkArrayInGroups } from 'app_vodafone_ziggo/util/util';
export default class VZAddOneNet extends VZAddBase {
    constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.prepareForm = this.prepareForm.bind(this);
        this.journey = getJourney();
    }

    connectedCallback() {
        this.querySelector('button').addEventListener('click', this.onSubmit);
    }

    disconnectedCallback() {
        this.querySelector('button').removeEventListener('click', this.onSubmit);
    }

    /**
     * @function getURL
     * @return {string} url - the add to cart endpoint
     * @description Returns the endpoint for adding products to the cart
     */
    static getURL() {
        return document.getElementById('product-form').action;
    }

    /**
     * @function submitLinkedProducts
     * @return {Promise} - A promise
     * @param {Array} products - Array of linked products
     * @param {boolean} inJourney - Is in journey
     * @description Submits all the products which are linked to the configured one.
     */
    submitLinkedProducts(products, inJourney) {
        const productChunks = chunkArrayInGroups(products, 3);
        const promises = productChunks.map((productData) => {
            const formData = new FormData();
            formData.set('productData', JSON.stringify(productData));
            formData.set('paid', this.constructor.getPAID().value);
            if (inJourney) {
                formData.set('belongsToGuidedJourney', true);
            }
            ['postalCode', 'houseNumber', 'houseNumberAddition', 'room'].forEach((field) => {
                formData.append(field, document.getElementById(`js-active-address-${field}`)?.value || '');
            });

            return fetch(this.constructor.getURL(), {
                method: 'POST',
                body: formData
            })
                .then((response) => {
                    if (response.status === 500) throw new Error('Network error');
                })
                .catch(this.constructor.handleNetworkError);
        });
        return Promise.all(promises);
    }

    /**
     * @function prepareForm
     * @description Adds the PAID and disables unrelated fields for the backend
     * @param {boolean} inJourney - Is in journey
     */
    prepareForm(inJourney) {
        this.form.appendChild(this.constructor.getPAID());
        [...this.form.querySelectorAll('input[name="addon"], input[name="tier"]')].forEach((field) => field.setAttribute('disabled', 'disabled'));

        const formData = new FormData(this.form);
        formData.set('addons', this.constructor.getAddons());

        const salesRepStatus = this.constructor.getSalesRepStatus();
        formData.set('allowSalesRepToContact', salesRepStatus);

        formData.set('configuratorType', 'one-net');

        formData.set('handsetMode', document.querySelector('#handset-mode')?.value ?? 'rent');

        if (inJourney) {
            formData.set('belongsToGuidedJourney', true);
        }

        ['city', 'street', 'room'].forEach((field) => {
            formData.append(field, document.querySelector(`.js-postcode-response input#${field}`)?.value || '');
        });

        ['postalCode', 'houseNumber', 'houseNumberAddition', 'room'].forEach((field) => {
            formData.append(field, document.getElementById(`js-active-address-${field}`)?.value || '');
        });

        fetch(this.form.action, {
            method: 'post',
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) throw new Error('Error');

                // After having added the product to the cart, show the "added to basket" modal
                const modal = document.getElementById(inJourney ? 'journey-modal' : 'add-to-basket-modal');
                if (!inJourney && response.hideAddToBasketModal) {
                    window.location.href = response.basketUrl;
                } else {
                    modal.render(); // Overriding default options.
                }

                /* eslint-disable no-underscore-dangle */
                if (response.ecommerceObject && window._ddm) {
                    window._ddm.trigger('ecommerce.add_to_cart', {
                        data: response.ecommerceObject.ecommerce.addToCart
                    });
                }
                /* eslint-enable no-underscore-dangle */
            })
            .catch(this.constructor.handleNetworkError)
            .finally(this.spinner.stop);
    }

    /**
     * @function onSubmit
     * @param {SubmitEvent} event - The form submit
     * @description Perform different actions based on whether the user is in editing mode or not
     */
    onSubmit(event) {
        event.preventDefault();
        this.classList.add('pe-none');
        /**
         * If we are in editing mode, remove the old location first
         * On success, submit the new form
         */
        if (this.editMode) {
            this.spinner.start();
            let url = document.querySelector('#remove-location').action;
            url = url.concat('&type=one-net');
            fetch(url, {
                method: 'POST'
            }).then(this.submitForm.bind(this));
        } else {
            this.submitForm.call(this);
        }
    }

    /**
     * @function submitPresets
     * @description Submits all products within a package
     * @returns {Promise} a promise
     */
    submitPresets() {
        const presets = document.querySelector('input[name="presets"]')?.value;
        if (presets) {
            const parsedPresets = JSON.parse(presets);
            const promises = Object.keys(parsedPresets)
                .reduce((prev, current) => {
                    prev.push(parsedPresets[current].products);
                    return prev;
                }, [])
                .flat()
                .map(({ ajaxAttributes }) => {
                    const formData = new FormData();
                    Object.keys(ajaxAttributes).forEach((key) => {
                        if (key === 'paid' && ajaxAttributes[key] === null) {
                            ajaxAttributes[key] = this.constructor.getPAID()?.value;
                        }

                        if (typeof ajaxAttributes[key] === 'object') {
                            ajaxAttributes[key] = JSON.stringify(ajaxAttributes[key]);
                        }

                        formData.append(key, ajaxAttributes[key]);
                    });

                    return fetch(this.form.action, {
                        method: 'post',
                        body: formData
                    });
                });

            return Promise.all(promises);
        }
        return Promise.all([]);
    }

    /**
     * @function submitForm
     * @description Submits all linked products, followed by the configured product
     */
    async submitForm() {
        const products = this.constructor.getAllProducts();
        if (products.length === 0) return;

        this.spinner.start();

        const inJourney = (document.querySelector('#internet')?.checked || document.querySelector('#business-mobile')?.checked) && document.getElementById('journey-modal');

        if (this.journey && this.journey.length > 0 && !inJourney) {
            // Journey was canceled in edit mode
            clearJourney();
        }

        await this.submitLinkedProducts(products, inJourney);
        await this.submitPresets();
        await this.prepareForm(inJourney);
        this.spinner.stop();
    }

    /**
     * @function getSalesRepStatus
     * @description Checks if sales is allowed to contact
     * @returns {boolean} hardware type
     */
    static getSalesRepStatus() {
        const element = document.getElementById('noAddon');
        return element ? element.checked : true;
    }
}

if (!window.customElements.get('vz-add-one-net')) {
    window.customElements.define('vz-add-one-net', VZAddOneNet);
}
